import { DatePipe } from "@angular/common";
import { Component, Inject, OnInit } from "@angular/core";
import { FormControl, FormGroup, Validators } from "@angular/forms";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { Router } from "@angular/router";
import { COMMA, ENTER } from "@angular/cdk/keycodes";
import { ToastrService } from "ngx-toastr";
import { Observable, forkJoin } from "rxjs";
import { map, startWith } from "rxjs/operators";
import { LoggingService } from "../../../../logging.service";
import { AssessmentCentralService } from "../../../../shared/services/assessment-central.service";
import { CommonService } from "../../../../shared/services/common.service";
import { FilterService } from "../../../../shared/services/filter.service";
import { IncidentTrackerService } from "../../../../shared/services/incident-tracker.service";
import { PrismService } from "../../../../shared/services/prism.service";
import textConfiguration from "../../../../../assets/static-text-configuration.json";
import { RiskConvergenceService } from "../../../../shared/services/risk-convergence.service";

@Component({
  selector: "incident-tracker-assessment-create-modal",
  templateUrl: "incident-tracker-assessment-create-modal.html",
  styleUrls: ["incident-tracker-assessment-create-modal.scss"],
  providers: [DatePipe],
})
export class IncidentTrackerAssessmentCreateModalDialogComponent implements OnInit {
  assessmentForm = new FormGroup({
    instance_title: new FormControl("", [Validators.required]),
    start_date: new FormControl("", [Validators.required]),
    audit_start_date: new FormControl("", [Validators.required]),
    end_date: new FormControl("", [Validators.required]),
    audit_end_date: new FormControl("", [Validators.required]),
    portfolio: new FormControl("", [Validators.required]),
    subportfolio: new FormControl("", [Validators.required]),
    program: new FormControl(""),
    project: new FormControl(""),
    vendor: new FormControl(""),
    applications: new FormControl([]),
    risk: new FormControl(),
    scope: new FormControl(""),
    approach: new FormControl(""),
  });
  appUserInputControl = new FormControl();
  commonText: any = (textConfiguration as any).common;
  moduleName: string = (textConfiguration as any)?.modules?.audits;
  appsList: any;
  isProjectSelected = false;
  file: any;
  fileName: any;
  selectedOwners: any = [];
  selectedAudience: any = [];
  filterOptionProgram: Observable<any[]>;
  filterOptionPortfolio: Observable<any[]>;
  filterOptionSubPortfolio: Observable<any[]>;
  filterOptionProject: Observable<any[]>;
  filterOptionVendor: Observable<any[]>;
  appFilteredOptions: Observable<string[]>;
  selectedApps: any;
  masterInformation: any;
  filteredOptions1: any;
  filteredOptions2: any;
  filteredOptions3: any;
  options1: any;
  options2: any;
  options3: any;
  subPortOptions: any;
  subPortfilteredOptions: any;
  programFilteredOptions: any;
  programOption: any;
  projectOption: any;
  vendorOption: any;
  displayLabels: any;
  separatorKeysCodes: number[] = [ENTER, COMMA];
  riskList: any[] = [];
  filterOptionRisk!: Observable<any[]>;
  selectedRisks: any[] = [];

  constructor(
    public dialogRef: MatDialogRef<IncidentTrackerAssessmentCreateModalDialogComponent>,
    private readonly assessmentCentralService: AssessmentCentralService,
    private readonly datePipe: DatePipe,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private readonly router: Router,
    private readonly toastrService: ToastrService,
    private readonly incidentTrackerService: IncidentTrackerService,
    private readonly prism: PrismService,
    private readonly commonService: CommonService,
    private readonly filterService: FilterService,
    private readonly loggingService: LoggingService,
    private readonly riskConvergenceService: RiskConvergenceService
  ) {}

  ngOnInit() {
    if (this.data?.moduleName) {
      this.moduleName = this.data.moduleName;
    }
    this.getDisplayLables();
    this.getMaster();
    this.getProgram();
    this.getProject();
    this.getVendor();
    if (this.data.openFrom === "project-central") {
      const programDetails = JSON.parse(
        localStorage.getItem("program") ?? "{}"
      );
      const selectedProjectDetails = JSON.parse(
        localStorage.getItem("selectedProject") ?? "{}"
      );
      this.assessmentForm.patchValue({
        portfolio: {
          id: programDetails.portfolio_id,
          name: programDetails.portfolio_name,
        },
        portfolio_id: programDetails.portfolio_id,
        subportfolio: {
          id: programDetails.subportfolio_id,
          name: programDetails.subportfolio_name,
        },
        subportfolio_id: programDetails.subportfolio_id,
        program: {
          id: programDetails.program_id,
          name: programDetails.program_name,
        },
        program_id: programDetails.program_id,
        project: {
          id: programDetails.project_id,
          project_name: selectedProjectDetails.project_name,
        },
        project_id: programDetails.project_id,
      });
      this.loadAppsForProject(programDetails.project_id);
      Object.keys(this.assessmentForm.controls).forEach((key) => {
        if (["portfolio", "subportfolio", "program", "project"].includes(key)) {
          const control = this.assessmentForm.get(key);
          if (control && control?.value != "") {
            control.disable();
          }
        }
      });
    }
  }

  getDisplayLables() {
    this.displayLabels = this.commonService.getLabels();
  }

  async getMaster() {
    const asyncOperations: Promise<void>[] = [];

    try {
      const res: any = await this.assessmentCentralService
        .getMasterForm()
        .toPromise();
      this.masterInformation = res?.records;

      this.masterInformation?.forEach((i: any) => {
        if (i.field_name === "opportunity_portfolio") {
          this.data.portfolio = i.field_status;
          this.data.disPortfolio = i.display_name;
          asyncOperations.push(
            this.filterService
              .getPortfolioList(this.moduleName)
              .toPromise()
              .then((resp: any) => {
                this.options1 = resp.records;
                this.filteredOptions1 = this.options1;
                if (!i.field_status) {
                  this.assessmentForm.removeControl("portfolio");
                }
              })
              .catch((error) => {
                this.loggingService.error("Error fetching portfolios: ", error);
              })
          );
        }
        if (i.field_name === "opportunity_sub_portfolio") {
          this.data.subPortfolio = i.field_status;
          this.subPortOptions = i.field_values;
          this.subPortfilteredOptions = this.subPortOptions;
          this.data.disSubPortfolio = i.display_name;
          if (!i.field_status) {
            this.assessmentForm.removeControl("subportfolio");
          }
        }
      });

      await Promise.all(asyncOperations);
      this.initAutoComplete();
      this.prefillFromRiskLog();
    } catch (error) {
      this.loggingService.error("Error fetching master form data: ", error);
    }
  }

  getProgram() {
    this.assessmentCentralService
      .getProgram(this.moduleName)
      .subscribe((res: any) => {
        this.programOption = res;
        const programControl = this.assessmentForm.get("program");
        if (programControl) {
          this.filterOptionProgram = programControl.valueChanges.pipe(
            startWith(""),
            map((value: any) => {
              if (typeof value === "string") {
                return value;
              } else {
                return value?.name;
              }
            }),
            map((name: any) => {
              if (name) {
                return this.programFilter(name);
              } else {
                return this.programOption.slice();
              }
            })
          );
        }
      });
  }

  getProject() {
    this.assessmentCentralService
      .getProject(this.moduleName)
      .subscribe((res: any) => {
        this.projectOption = res;

        const projectControl = this.assessmentForm.get("project");

        if (projectControl) {
          this.filterOptionProject = projectControl.valueChanges.pipe(
            startWith(""),
            map((value: any) =>
              typeof value === "string" ? value : value?.project_name
            ),
            map((name: string) =>
              name ? this.projectFilter(name) : this.projectOption.slice()
            )
          );

          // When project is actually SELECTED
          projectControl.valueChanges.subscribe((value: any) => {
            if (value && value.id) {
              this.isProjectSelected = true;

              // Fetch applications for selected project
              this.prism
                .getApplicationNames(`?project_id=${value.id}`)
                .subscribe((res: any) => {
                  if (res?.length > 0) {
                    this.setAppData(res);
                  } else {
                    this.setAppData([]); // clear chips if no apps
                  }
                });
              this.loadRisksForProject(value.id);
            } else {
              this.isProjectSelected = false;
            }
          });
        }
      });
  }

  private async prefillFromRiskLog() {
    if (this.data?.openFrom !== "risklog") return;

    const { portfolio_id, subportfolio_id, program_id, project_id, risk_ids } =
      this.data;

    const portfolioObj = this.filteredOptions1?.find(
      (p: any) => Number(p.id) === Number(portfolio_id)
    );

    if (!portfolioObj) return;

    const responses: any = await forkJoin([
      this.filterService.getSubportfolioList(portfolio_id, this.moduleName),
      this.filterService.getProgram(subportfolio_id, this.moduleName),
      this.filterService.getProject(
        subportfolio_id,
        program_id,
        this.moduleName
      ),
      this.riskConvergenceService.getFilteredRiskTracker(
        0,
        1000,
        `&project=${project_id}`,
        this.moduleName
      ),
    ]).toPromise();

    const [subResp, programResp, projectResp, riskResp] = responses;

    this.subPortOptions = subResp.records || [];
    this.programOption = programResp.records || [];
    this.projectOption = projectResp.records || [];
    this.riskList = riskResp.records || [];

    const subportfolioObj = this.subPortOptions.find(
      (sp: any) => Number(sp.id) === Number(subportfolio_id)
    );
    const programObj = this.programOption.find(
      (pg: any) => Number(pg.id) === Number(program_id)
    );
    const projectObj = this.projectOption.find(
      (pr: any) => Number(pr.id) === Number(project_id)
    );

    this.assessmentForm.patchValue({
      portfolio: portfolioObj,
      subportfolio: subportfolioObj,
      program: programObj,
      project: projectObj,
    });

    if (this.isMultiRisks) {
      // Multi: pre-select all risks matching passed risk_ids
      this.selectedRisks = this.riskList.filter((r: any) =>
        (risk_ids as number[]).includes(r.risk_id)
      );
    }
  }

  private riskFilter(value: string): any[] {
    const filterValue = value.toLowerCase();

    return this.riskList.filter((r: any) =>
      r?.risk?.toLowerCase().includes(filterValue)
    );
  }

  private loadRisksForProject(projectId: number) {
    if (!projectId) return;

    const queryParams = `&project=${projectId}`;

    this.riskConvergenceService
      .getFilteredRiskTracker(0, 1000, queryParams, this.moduleName)
      .subscribe(
        (res: any) => {
          this.riskList = res?.records || [];

          const riskControl = this.assessmentForm.get("risk");

          if (riskControl) {
            this.filterOptionRisk = riskControl.valueChanges.pipe(
              startWith(""),
              map((value: any) =>
                typeof value === "string" ? value : value?.risk
              ),
              map((name: string) =>
                name ? this.riskFilter(name) : this.riskList.slice()
              )
            );
          }
        },
        (error) => {
          this.loggingService.error("Error loading risks: ", error);
        }
      );
  }

  displayFnRisk(data: any) {
    return data?.risk || "";
  }

  getVendor() {
    this.assessmentCentralService.getVendor().subscribe((res: any) => {
      this.vendorOption = res.records[0].field_values;
      const vendorControl = this.assessmentForm.get("vendor");
      if (vendorControl) {
        this.filterOptionVendor = vendorControl.valueChanges.pipe(
          startWith(""),
          map((value: any) => {
            if (typeof value === "string") {
              return value;
            } else {
              return value?.name;
            }
          }),
          map((name: any) => {
            if (name) {
              return this.vendorFilter(name);
            } else {
              return this.vendorOption.slice();
            }
          })
        );
      }
    });
  }

  onSelectionChangePortfolio(event: any) {
    const portFolioId = event.source.value.id;
    this.subPortfilteredOptions = [];
    if (!portFolioId) {
      return;
    }
    this.assessmentForm.patchValue({
      subportfolio: "",
    });
    this.filterService
      .getSubportfolioList(portFolioId, this.moduleName)
      .subscribe((resp: any) => {
        this.subPortOptions = resp.records;
        this.subPortfilteredOptions = this.subPortOptions;
        const subportfolioControl = this.assessmentForm.get("subportfolio");
        if (subportfolioControl) {
          this.filterOptionSubPortfolio = subportfolioControl.valueChanges.pipe(
            startWith(""),
            map((value: any) => {
              if (typeof value === "string") {
                return value;
              } else {
                return value?.name;
              }
            }),
            map((name: any) => {
              if (name) {
                return this.subPortfolioFilter(name);
              } else {
                return this.subPortfilteredOptions.slice();
              }
            })
          );
        }
      });
  }

  onSelectionChangeSubportfolio(e: any) {
    const subportFolioId = e.source.value.id;
    this.programOption = [];
    if (!subportFolioId) {
      return;
    }
    this.assessmentForm.patchValue({
      program: "",
    });

    this.filterService
      .getProgram(e.source.value.id, this.moduleName)
      .subscribe(async (resp: any) => {
        this.programOption = resp.records;
        const programControl = this.assessmentForm.get("program");
        if (programControl) {
          this.filterOptionProgram = programControl.valueChanges.pipe(
            startWith(""),
            map((value: any) => {
              if (typeof value === "string") {
                return value;
              } else {
                return value?.name;
              }
            }),
            map((name: any) => {
              if (name) {
                return this.programFilter(name);
              } else {
                return this.programOption.slice();
              }
            })
          );
        }
      });
  }

  onSelectionChangeProgram(e: any) {
    const programId = e.source.value.id;
    const subportfolioId: any =
      this.assessmentForm.get("subportfolio")?.value?.id;
    this.projectOption = [];
    if (!programId || !subportfolioId) {
      return;
    }
    this.assessmentForm.patchValue({
      project: "",
    });
    this.filterService
      .getProject(subportfolioId, programId, this.moduleName)
      .subscribe(async (resp: any) => {
        this.projectOption = resp.records;
        const projectControl = this.assessmentForm.get("project");
        if (projectControl) {
          this.filterOptionProject = projectControl.valueChanges.pipe(
            startWith(""),
            map((value: any) => {
              if (typeof value === "string") {
                return value;
              } else {
                return value?.name;
              }
            }),
            map((name: any) => {
              if (name) {
                return this.projectFilter(name);
              } else {
                return this.projectOption.slice();
              }
            })
          );
        }
      });
  }

  createAssessment() {
    const formData: any = new FormData();
    const postdata = this.assessmentForm.getRawValue();

    formData.append("instance_title", postdata.instance_title);
    formData.append(
      "start_date",
      this.datePipe.transform(postdata.start_date, "YYYY-MM-dd")
    );
    formData.append(
      "end_date",
      this.datePipe.transform(postdata.end_date, "YYYY-MM-dd")
    );
    formData.append(
      "audit_start_date",
      this.datePipe.transform(postdata.audit_start_date, "YYYY-MM-dd")
    );
    formData.append(
      "audit_end_date",
      this.datePipe.transform(postdata.audit_end_date, "YYYY-MM-dd")
    );
    this.selectedOwners.forEach((id: any) => {
      formData.append("respondent", id);
    });

    this.selectedAudience.forEach((id: any) => {
      formData.append("auditor", id);
    });
    formData.append("template", this.data.template_id);
    formData.append("portfolio", postdata.portfolio.id);
    formData.append("sub_portfolio", postdata.subportfolio.id);
    if (postdata.program?.id) {
      formData.append("program", postdata.program.id);
    }

    if (postdata.project?.id) {
      formData.append("project", postdata.project.id);
    }
    formData.append("vendor", postdata.vendor.id ?? null);
    formData.append("scoring_methodology", "");
    formData.append("scope", postdata.scope ?? "");
    formData.append("approach", postdata.approach ?? "");

    this.selectedApps?.forEach((app: any) => {
      formData.append("applications_ids", app.id.toString());
    });

    this.data.clause_ids?.forEach((clauseId: any) => {
      formData.append("clause_id", clauseId);
    });

    if (this.isMultiRisks) {
      this.selectedRisks.forEach((r: any) => {
        formData.append("risk_id", r.risk_id);
      });
    }

    this.incidentTrackerService
      .createAuditInstance(formData, this.moduleName)
      .subscribe((res: any) => {
        this.toastrService.success("Audit instance created successfully");
        this.dialogRef.close(true);
        this.openInstance(res);
      });
  }

  removeApp(i: any) {
    this.selectedApps.splice(i, 1);
  }

  setAppData(appsList: any) {
    this.selectedApps = [];
    this.appsList = appsList;
    if (this.data.clause?.related_applications) {
      this.data.clause.related_applications.forEach((element: any) => {
        this.appsList.forEach((elm: any) => {
          if (elm.id === element.id) {
            this.selectedApps.push(elm);
          }
        });
      });
    }
    this.appFilteredOptions = this.appUserInputControl.valueChanges.pipe(
      startWith(""),
      map((value) => this._filterApp(value))
    );
  }

  private _filterApp(value: any): any {
    if (typeof value === "string") {
      const filterValue = value.toLowerCase();
      const removeSelected = this.appsList.filter(
        (el: any) => !this.selectedApps.includes(el)
      );
      return removeSelected.filter((app: any) =>
        app?.name?.toLowerCase().includes(filterValue)
      );
    }
    return null;
  }

  addApp(e: any) {
    this.selectedApps.push(e);
    this.appUserInputControl.setValue("");
  }

  openInstance(res: any) {
    const currentPageUrl = `${this.router.url.split("?")[0]}?activeTab=${
      this.data.activeTab
    }`;
    if (this.data.openFrom === "project-central") {
      this.router.navigate(["incidentracker/instance"], {
        queryParams: { id: res.id, goBack: btoa(currentPageUrl) },
      });
      return;
    }
    if (this.data.openFrom === "risklog") {
      return;
    }
    this.router.navigate(["incidentracker/instance"], {
      queryParams: { id: res.id },
    });
  }

  onFileChange(event: any) {
    this.file = event.target.files[0];
    this.fileName = this.file.name;
    event.target.value = "";
  }

  initAutoComplete(): any {
    const portfolioControl = this.assessmentForm.get("portfolio");
    if (portfolioControl) {
      this.filterOptionPortfolio = portfolioControl.valueChanges.pipe(
        startWith(""),
        map((value: any) => {
          if (typeof value === "string") {
            return value;
          } else {
            return value?.name;
          }
        }),
        map((name: any) => {
          if (name) {
            return this.portfolioFilter(name);
          } else {
            return this.filteredOptions1.slice();
          }
        })
      );
    }
  }

  private programFilter(value: string): any {
    const filterValue = value.toLowerCase();
    return this.programOption.filter((i: any) =>
      i.name.toLowerCase().includes(filterValue)
    );
  }
  private portfolioFilter(value: string): any {
    const filterValue = value.toLowerCase();
    return this.filteredOptions1.filter((i: any) =>
      i.name.toLowerCase().includes(filterValue)
    );
  }
  private vendorFilter(value: string): any {
    const filterValue = value.toLowerCase();
    return this.vendorOption.filter((i: any) =>
      i.name.toLowerCase().includes(filterValue)
    );
  }
  private subPortfolioFilter(value: string): any {
    const filterValue = value.toLowerCase();
    return this.subPortOptions.filter((i: any) =>
      i.name.toLowerCase().includes(filterValue)
    );
  }

  private projectFilter(value: string): any {
    const filterValue = value.toLowerCase();
    return this.projectOption.filter((i: any) =>
      i.project_name.toLowerCase().includes(filterValue)
    );
  }

  private loadAppsForProject(projectId: number) {
    if (!projectId) return;

    this.isProjectSelected = true;

    this.prism
      .getApplicationNames(`?project_id=${projectId}`)
      .subscribe((res: any) => {
        if (res.length > 0) {
          this.setAppData(res);
        } else {
          this.setAppData([]);
        }
      });
  }

  displayFn(data: any) {
    if (data?.name) {
      return data.name;
    } else {
      return "";
    }
  }

  displayFnProject(data: any) {
    if (data?.project_name) {
      return data.project_name;
    } else {
      return "";
    }
  }

  userdata(e: any) {
    this.selectedOwners = e;
  }

  audiencedata(e: any) {
    this.selectedAudience = e;
  }

  goBackModal(): void {
    this.dialogRef.close(1);
  }

  closeModal(): void {
    this.dialogRef.close();
  }

  get isRisklogMode(): boolean {
    return this.data?.openFrom === "risklog";
  }

  get isMultiRisks(): boolean {
    return !!this.data?.isMultiRisks;
  }
}
